/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2022. All rights reserved
------------------------------------------------------------------------------------------------- */
import { css, html, LitElement } from 'lit-element';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import { baseFontSize } from '../../utils/base-font-size.js';
import { dispatchCustomEvent } from '../../utils/custom-event-dispatcher.js';

/**
 * @prop {String} label - Defines the text shown below the item.
 * @prop {String} info - Defines the text shown inside the item.
 * @prop {String} color - Define the color of the ring shown around the item.
 * @prop {String} icon - For user actions, defines the icon shown inside the item.
 * @prop {Number} progress - Defines the progress of an individual item, which is reflected in the filling of the ring around it.
 * @prop {Boolean} indeterminate - If set to true, the progress bar will spin indefinitely until the property is set back to false.
 * @prop {Boolean} active - Defines whether an item is currently selected by the user or not.
 * @prop {Boolean} current - Defines whether the item is currently being executed by the scanner.
 * @prop {Boolean} disabled - If set to true, the styles of the item will adapt and it will not allow mouse or touch interactions.
 * @prop {Boolean} readonly - If set to true, the item will not allow mouse or touch interactions.
 * @prop {Boolean} messageVisible - If set to true, the popover message will be shown.
 * @prop {Boolean} repeat - If set to true, the workline item will show the repeat animation. This animation only appears initially when the workline-item is attached to the DOM for the first time.
 * @prop {Boolean} float - If set to true, the workline item will float on the left / right position depending on its position.
 * @prop {Boolean} thumbnail - 	If set, a thumbnail is shown in the workline item.
 * @prop {Boolean} new - Flags an workline-item as new. The new property is removed after the item gets active.
 * @prop {Boolean} type - Defines the type of workline-item that will be displayed. Accepted value information.
 * @slot badge - Takes icons or badge to indicate status related to the item.
 * @slot info - Takes icons or text that provides informtion such as delay time or voice commands. Is hown on the left side of the item.
 * @slot message - Is shown when message-visible property is set to true. Contains any HTML content that composes the message body (e.g. plain text, icons, buttons.
 */
export class SHWorklineItem extends LitElement {
  static get properties() {
    return {
      label: { type: String, reflect: true },
      info: { type: String, reflect: true },
      color: { type: String, reflect: true },
      icon: { type: String, reflect: true },
      progress: { type: Number, reflect: true },
      indeterminate: { type: Boolean, reflect: true },
      active: { type: Boolean, reflect: true },
      current: { type: Boolean, reflect: true },
      disabled: { type: Boolean, reflect: true },
      readonly: { type: Boolean, reflect: true },
      messageVisible: { type: Boolean, reflect: true, attribute: 'message-visible' },
      repeat: { type: Boolean, reflect: true },
      float: { type: Boolean, reflect: true },
      thumbnail: { type: String, reflect: true },
      new: { type: Boolean, reflect: true },
      type: { type: String, reflect: true },
      /** @ignore */
      badge: { type: String, reflect: true },
      /** @ignore */
      _emptyMessage: { type: Boolean, value: true },
      /** @ignore */
      _emptyinfo: { type: Boolean, reflect: true, attribute: 'empty-info' },
      /** @ignore */
      _emptyinforight: { type: Boolean, reflect: true, attribute: 'empty-info-right' },
    };
  }
  static get styles() {
    return [
      css`
        :host {
          z-index: 0;
          position: relative;
          position: -webkit-sticky;
          display: flex;
          /* flex-direction: column; */
          align-items: center;
          justify-content: center;
          height: fit-content;
          width: fit-content;
          background: rgba(var(--ui-1), var(--opacity-5));
          border-radius: 2.5rem;
          text-align: center;
          box-sizing: border-box;
          transition: var(--transition-time) all ease-in-out, left 0s linear, right 0s linear,
            top 0s linear, 'z-index' 0s linear, 'margin' 0s linear, 'background-image' 0s linear,
            'box-shadow' 0s linear;
          cursor: pointer;
        }
        :host([type='information']) {
          background: transparent;
        }
        :host([type='information']) .info-right-wrapper :host([type='information']) .info-wrapper,
        :host([type='information']) .progress-wrapper,
        :host([type='information']) .item-info,
        :host([type='information']) .thumbnail-wrapper,
        :host([type='information']) .badge-wrapper,
        :host([type='information']) .item-label {
          display: none;
        }
        :host([type='information']) .item-wrapper {
          margin: 0rem;
          background: transparent;
          height: 3rem;
          width: 3rem;
        }
        :host([type='information']) #itemIcon {
          line-height: 2.25rem;
          color: rgba(var(--ui-1), var(--opacity-3));
        }
        /* item wrapper */
        .item-wrapper {
          margin: 0.75rem;
          position: relative;
          background: rgba(var(--ui-6), var(--opacity-1));
          display: flex;
          align-items: center;
          justify-content: center;
          height: 3.5rem;
          width: 3.5rem;
          box-sizing: border-box;
          border-radius: 50%;
          transition: var(--transition-time) all ease-in-out;
        }
        /* icon */
        :host(:not([icon])) #itemIcon {
          display: none;
        }
        /* thumbnail */
        :host(:not([thumbnail])) .thumbnail-wrapper {
          display: none;
        }
        .thumbnail-wrapper {
          position: absolute;
          height: 3rem;
          width: 3rem;
          overflow: hidden;
          border-radius: 50%;
          display: block;
        }
        #itemThumbnail {
          position: relative;
          width: 100%;
          height: 100%;
          background-size: contain;
          opacity: var(--opacity-3);
          top: 50%;
          left: 50%;
          image-rendering: -webkit-optimize-contrast;
          -webkit-transform: translateY(-50%) translateX(-50%);
          -ms-transform: translateY(-50%) translateX(-50%);
          transform: translateY(-50%) translateX(-50%);
        }
        :host([active]) #itemThumbnail {
          opacity: var(--opacity-1);
        }
        /* label, info */
        .item-label {
          position: absolute;
          bottom: -2.5rem;
          height: 2rem;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          left: 0rem;
        }
        .item-label,
        .item-info {
          color: var(--text-secondary);
        }
        :host([active]) .item-label,
        :host([active]) .item-info {
          color: var(--text-primary);
        }
        .item-info,
        #itemIcon {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        :host([icon]) .item-info {
          display: none;
        }
        /* triangles */
        .triangles {
          width: 0;
          height: 4rem;
          position: absolute;
          border-left: 0.375rem solid transparent;
          border-right: 0.375rem solid transparent;
          border-bottom: 0.5rem solid rgba(var(--ui-1), var(--opacity-1));
          border-top: 0.5rem solid rgba(var(--ui-1), var(--opacity-1));
        }
        :host(:not([current])) .triangles {
          display: none;
        }
        /* color ring */
        .color-ring {
          position: absolute;
          top: 0rem;
          left: 0rem;
          border: 0.1875rem dotted transparent;
          border-radius: 50%;
          height: 3.5rem;
          width: 3.5rem;
          box-sizing: border-box;
          transition: var(--transition-time) all ease-in-out;
        }
        :host([progress='100']) .color-ring {
          border-color: transparent !important;
        }
        :host([progress='100'][new]) #progress-ring {
          stroke: rgba(var(--ui-0), var(--opacity-1));
        }
        /* progress ring */
        .progress-wrapper {
          height: 3.5rem;
          width: 3.5rem;
          position: absolute;
          transition: var(--transition-time) all ease-in-out;
          opacity: var(--opacity-3);
        }
        svg {
          stroke-width: 0.25rem;
          fill: transparent;
          cx: 50%;
          cy: 50%;
          transition: var(--transition-time) all ease-in-out;
          transform: rotate(-90deg);
        }
        :host([progress='100']) svg {
          stroke-width: 0.1875rem;
          r: 1.53125rem;
        }
        :host(:not([indeterminate])) .progress-wrapper > svg:nth-child(2) {
          animation: none;
        }
        /* badges */
        :host(:not([empty-info])[active]) .badge-wrapper,
        :host(:not([empty-info-right])[active]) .badge-wrapper {
          top: -0.3rem;
          right: -0.5rem;
        }
        :host(:not([empty-info-right])[current]) .info-right-wrapper,
        :host(:not([empty-info-right])[active]) .info-right-wrapper {
          margin-right: 0.5rem;
        }
        .badge-wrapper-current {
          display: flex;
          position: absolute;
          right: -0.25rem;
          top: -0.25rem;
          transition: var(--transition-time) all ease-in-out;
        }
        .badge-wrapper {
          display: flex;
          position: absolute;
          right: 0.5rem;
          top: 0.5rem;
          transition: var(--transition-time) all ease-in-out;
        }
        .badge-wrapper-current > ::slotted(sh-icon),
        .badge-wrapper > ::slotted(sh-icon) {
          transition: 0s all ease-in-out;
          --sh-icon-size-m: 1.5rem;
          --sh-icon-font-size-m: 1.5rem;
        }
        :host(:not([empty-info-right])[active]) .badge-wrapper > ::slotted(sh-icon),
        :host(:not([empty-info])[active]) .badge-wrapper > ::slotted(sh-icon) {
          line-height: 2rem;
          font-size: 2rem;
          max-height: 2rem;
          min-height: 2rem;
          min-width: 2rem;
        }
        /* info slot */
        .info-right-wrapper,
        .info-wrapper {
          opacity: 0;
          width: auto;
          display: flex;
          max-width: 0rem;
          overflow: hidden;
          align-items: center;
          transition: var(--transition-time) max-width ease-in-out;
        }
        :host([active]) .info-right-wrapper,
        :host([current]) .info-right-wrapper,
        :host([active]) .info-wrapper,
        :host([current]) .info-wrapper {
          opacity: 1;
          max-width: 12.5rem;
        }
        .info-wrapper > ::slotted(*:first-child) {
          margin-left: 1rem;
        }
        .info-wrapper > ::slotted(*:not(:first-child)) {
          margin-left: 0.5rem;
        }
        .info-right-wrapper > ::slotted(*) {
          margin-right: 0.5rem;
        }
        /* active */
        :host([active]) {
          background: rgba(var(--ui-1), var(--opacity-5));
          box-shadow: inset 0rem 0rem 0rem 0.0625rem rgba(var(--ui-1), var(--opacity-1));
        }
        :host([active][type='information']) {
          background: transparent;
          box-shadow: none;
        }
        :host([active]) .item-wrapper {
          background: rgba(var(--ui-7), var(--opacity-1));
        }
        :host([type='information'][active]) .item-wrapper {
          background: transparent;
        }
        :host([active]) .progress-wrapper {
          opacity: var(--opacity-1);
        }
        /* readonly */
        :host([disabled]),
        :host([readonly]) {
          pointer-events: none;
          cursor: default;
        }
        /* disabled */
        :host([disabled]) .item-label,
        :host([disabled]) .item-info {
          color: var(--text-disabled);
        }
        :host([disabled]) .progress-wrapper {
          opacity: var(--opacity-5);
        }
        :host([disabled]) {
          opacity: var(--opacity-4);
        }
        /* indeterminate */
        :host([indeterminate]) #progress-ring {
          stroke-dasharray: 41, 164;
        }
        :host([indeterminate]) .progress-wrapper > svg:nth-child(2) {
          animation: indeterminate 1.5s linear infinite;
        }
        @keyframes indeterminate {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        /* message */
        sh-popover {
          width: fit-content;
          width: -moz-fit-content;
          height: auto;
          cursor: default;
          text-align: left;
          max-width: 75rem;
          max-height: 50rem;
          transition: 0s transform linear;
        }
        :host(.no-transitions),
        :host(.no-transitions) *,
        :host(.no-transitions) * > ::slotted(*) {
          transition: all 0s linear;
        }
        :host(:not([disabled]):not([readonly]).repeat-animate) {
          animation-name: repeat-animate;
          animation-duration: 0.6s;
          animation-timing-function: cubic-bezier(0, 0.5, 1, 0.5);
        }
        :host(:not([disabled]):not([readonly]).repeat-animate) .item-label {
          animation-name: fade-in;
          animation-duration: inherit;
          animation-timing-function: inherit;
        }
        :host(:not([disabled]):not([readonly]).repeat-animate) .item-wrapper {
          animation-name: shrink-expand-shrink;
          animation-duration: inherit;
          animation-timing-function: inherit;
        }

        @keyframes fade-in {
          0% {
            opacity: 0;
            visibility: hidden;
          }
          80% {
            opacity: 0;
            visibility: hidden;
          }
          100% {
            opacity: 1;
            visibility: visible;
          }
        }
        @keyframes shrink-expand-shrink {
          0% {
            transform: scale(0.7);
          }
          50% {
            transform: scale(1.2);
          }
          100% {
            transform: scale(1);
          }
        }
        @keyframes repeat-animate {
          0% {
            transform: translate(-2rem, 0.9375rem);
            height: calc(0.7 * 5rem);
            width: calc(0.7 * 5rem);
            margin-left: 0rem;
            margin-right: 0rem;
            border-radius: 50%;
          }
          50% {
            transform: translate(0rem, -0.5rem);
            height: calc(1.2 * 5rem);
            width: calc(1.2 * 5rem);
            margin-left: 0.5rem;
            margin-right: -0.5rem;
            border-radius: 50%;
          }
          100% {
            transform: translate(0rem, 0rem);
            height: calc(5rem);
            width: calc(5rem);
            border-radius: 50%;
          }
        }
        .progress-ring-svg {
          transform: rotate(-90deg);
          position: absolute;
          left: 0;
          top: 0;
        }
        :host([type='information']) sh-popover[position='top'] {
          transform: translateY(-1rem);
        }
        :host(.leftfix[float]) {
          position: absolute;
          z-index: 3;
          margin: 0;
          background-color: rgba(var(--ui-5), var(--opacity-1));
        }
        :host(.rightfix[float]) {
          position: absolute;
          z-index: 3;
          margin: 0;
          background-color: rgba(var(--ui-5), var(--opacity-1));
        }
        .item-info {
          line-height: 1.5rem;
        }
      `,
    ];
  }
  render() {
    return html`
      <div class="info-wrapper">
        <slot
          name="info"
          id="info"
          @slotchange=${(e) => this._updateEmptySlotFlag(e.target, '_emptyinfo')}
        ></slot>
      </div>
      <!-- Adding on-click="stopEvent" as a fix for the bug 22078 in Azure Devops -->
      <!-- Bug link : https://dev.azure.com/shs-ti-ux/SHUI/_workitems/edit/22078 -->
      <sh-popover
        passive
        .visible="${this.messageVisible && !this._emptyMessage}"
        @visible-changed=${(e) => {
          e.target.visible = this.messageVisible && !this._emptyMessage;
        }}
        sticky
        @click="${this.stopEvent}"
        .target="${this._getPopoverTarget()}"
      >
        <slot
          name="message"
          id="messageSlot"
          @slotchange=${(e) => this._updateEmptySlotFlag(e.target, '_emptyMessage')}
        ></slot>
      </sh-popover>
      <div class="item-wrapper">
        <div class="progress-wrapper">
          <svg style="height:3.5rem; width:3.5rem;" class="progress-ring-svg">
            <circle
              id="dash-ring"
              style="cx:1.75rem; cy:1.75rem; r:1.625rem;"
              stroke-dasharray="0, 5.83"
              stroke="${this.color}"
              fill="transparent"
              stroke-width="0.1875rem"
              stroke-linecap="round"
            />
          </svg>
          <svg style="height:3.5rem; width:3.5rem;" class="progress-ring-svg">
            <circle
              style="cx:1.75rem; cy:1.75rem; r:1.625rem;"
              id="progress-ring"
              stroke="${this.color}"
              fill="transparent"
            />
          </svg>
        </div>
        ${this.info ? html` <sh-text size="body-1" class="item-info">${this.info}</sh-text>` : ''}
        ${this.thumbnail
          ? html` <div class="thumbnail-wrapper">
              <div id="itemThumbnail" style="background-image:url('${this.thumbnail}');"></div>
            </div>`
          : ''}
        ${this.icon ? html` <sh-icon id="itemIcon" icon=${this.icon}></sh-icon>` : ''}
        ${this.current ? html` <div class="triangles"></div>` : ''}
      </div>
      <div class="info-right-wrapper">
        <slot
          name="info-right"
          id="infoRight"
          @slotchange=${(e) => this._updateEmptySlotFlag(e.target, '_emptyinforight')}
        ></slot>
      </div>
      <div class="badge-wrapper">
        <slot name="badge" id="badgeSlot"></slot>
      </div>
      ${this.label ? html`<sh-text size="body-2" class="item-label">${this.label}</sh-text>` : ''}
    `;
  }

  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has('progress')) {
      this._progressChanged();
    }
    if (changedProperties.has('repeat')) {
      this._handleRepeatChange();
    }
    if (changedProperties.has('float')) {
      this._handleFloatChange();
    }
    const listOfProperties = [
      'label',
      'info',
      'color',
      'icon',
      'indeterminate',
      'active',
      'current',
      'disabled',
      'readonly',
      'messageVisible',
      'thumbnail',
      'new',
      'type',
    ];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }
  constructor() {
    super();
    this.new = false;
    this.repeat = false;
    this.progress = '0';
    this.indeterminate = false;
    this.messageVisible = false;
    this._emptyMessage = true;
    this.active = false;
    this.readonly = false;
    this.disabled = false;
    this._emptyinfo = true;
    this._emptyinforight = true;
  }
  firstUpdated(changedProperties) {
    if (changedProperties.has('repeat')) {
      this._handleRepeatChange();
    }
    const infowrapper = this.shadowRoot.querySelector('.info-wrapper');
    infowrapper.removeEventListener('transitionend', this._transitionendHandler);
    infowrapper.addEventListener('transitionend', this._transitionendHandler);

    const computedSize = baseFontSize / 16;
    //this calulation given designer, for dotted circles in progress
    const dashRing = this.shadowRoot.querySelector('#dash-ring');
    if (dashRing) {
      dashRing.setAttribute('stroke-dasharray', `0, ${computedSize * 5.83}`);
    }
  }
  connectedCallback() {
    super.connectedCallback();
    this._transitionendListener = this._transitionendHandler.bind(this);
  }
  _transitionendHandler() {
    dispatchCustomEvent(this, 'workline-item-size-changed');
  }
  _updateEmptySlotFlag(slot, emptySlotFlag) {
    this[emptySlotFlag] =
      slot.assignedNodes({
        flatten: true,
      }).length === 0;
  }
  switchOffTransitions() {
    this.classList.add('no-transitions');
  }
  resetTransitions() {
    this.classList.remove('no-transitions');
  }
  _handleFloatChange() {
    dispatchCustomEvent(this, 'workline-item-float-changed');
  }
  _handleRepeatChange() {
    if (this.repeat) {
      this.classList.add('repeat-animate');
      this.classList.add('no-transitions');
      // the animation class is removed here inorder to avoid
      // retriggering of the animation when the workline item
      // is removed from the DOM.
      this.addEventListener('animationend', function animationEndListener() {
        this.classList.remove('repeat-animate');
        this.classList.remove('no-transitions');
        this.removeEventListener('animationend', animationEndListener);
        dispatchCustomEvent(this, 'workline-animation-ended');
      });
    }
  }

  /**
   * Adding on-click="stopEvent" as a fix for the bug 22078 in Azure Devops.
   * Bug link : https://dev.azure.com/shs-ti-ux/SHUI/_workitems/edit/22078
   */
  stopEvent(e) {
    e.stopPropagation();
  }
  _progressChanged() {
    const computedSize = baseFontSize / 16;
    //this calulation given designer, for dotted circles in progress
    const progressRing = this.shadowRoot.querySelector('#progress-ring');
    if (progressRing) {
      progressRing.setAttribute(
        'stroke-dasharray',
        `${this.progress * 1.64 * computedSize}, ${computedSize * 164}`
      );
    }
  }
  _getPopoverTarget() {
    return this;
  }
}
customElements.define('sh-workline-item', SHWorklineItem);
